import { useNavigate, useParams } from 'react-router-dom';
import { ProductTransactionExportConfigurationContext } from '../../../../../../../context/ProductTransactionExportConfigurationContext';
import { useContext, useEffect, useState } from 'react';
import {
  ProductTransactionExportConfiguration,
  ProductTransactionExportConfigurationField,
  ProductTransactionExportConfigurationInput,
} from '../../../../../../../types/product_transaction_export_configuration';
import { ProductTransactionExportConfigurationRequest } from '../../../../../../../requests/productTransactionExportConfiguration.request';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { Routing } from '../../../../../../../util/routes.util';
import { BackButton, ButtonVariant, DeleteButton, UpdateButton } from '../../../../../../components/Button';
import React from 'react';
import DeleteProductTransactionExportConfigurationModal from './Modals/DeleteProductTransactionExportConfigurationModal';
import ProductTransactionExportConfigurationForm, {
  DraggableProductTransactionExportFieldIdentifier,
} from '../Common/ProductTransactionExportConfigurationForm';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface UpdateProductTransactionExportConfigurationPaneProps {}

export default function UpdateProductTransactionExportConfigurationPane({}: UpdateProductTransactionExportConfigurationPaneProps) {
  const id = useParams()['id'] || '';
  if (!id) return null; // TODO: Entity not found

  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);

  const {
    productTransactionExportConfigurations,
    setProductTransactionExportConfigurations,
    productTransactionExportConfigurationsLoading,
  } = useContext(ProductTransactionExportConfigurationContext);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const existing = productTransactionExportConfigurations.get(id);

  const [input, setInput] = useState<ProductTransactionExportConfigurationInput>(
    new ProductTransactionExportConfigurationInput(
      existing || ProductTransactionExportConfiguration.default(currentCompany.id),
    ),
  );

  useEffect(() => {
    if (!productTransactionExportConfigurationsLoading && existing) {
      setInput(new ProductTransactionExportConfigurationInput(existing));
    }
  }, [productTransactionExportConfigurationsLoading]);

  const [update, { loading: updateLoading }] = useRequest(ProductTransactionExportConfigurationRequest.update);

  const handleUpdate = async (
    configuration: ProductTransactionExportConfigurationInput,
    items: DraggableProductTransactionExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ProductTransactionExportConfigurationField>(items);

    const result = await update({ data: [configuration.forUpdate()] });
    if (!result) return;

    const config = result[0];
    productTransactionExportConfigurations.set(config.id, new ProductTransactionExportConfiguration(config));
    setProductTransactionExportConfigurations(new Map(productTransactionExportConfigurations));
    navigate(Routing.stock.history.settings.report.overview());
  };

  const footer = (
    configuration: ProductTransactionExportConfigurationInput,
    items: DraggableProductTransactionExportFieldIdentifier[],
  ) => {
    return (
      <div className='flex justify-between'>
        <div>
          <DeleteButton
            disabled={productTransactionExportConfigurationsLoading || updateLoading}
            onClick={() => setOpenDeleteModal(true)}
          />
        </div>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={updateLoading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.stock.products.settings.report.overview())}
          />
          <UpdateButton
            loading={updateLoading}
            disabled={productTransactionExportConfigurationsLoading || !configuration.filename || !configuration.name}
            onClick={() => handleUpdate(configuration, items)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DeleteProductTransactionExportConfigurationModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        ids={new Set([id])}
      />
      <ProductTransactionExportConfigurationForm
        loading={productTransactionExportConfigurationsLoading}
        input={input}
        setInput={setInput}
        footer={footer}
      />
    </>
  );
}
